import { useMutation } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import { Alert, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FullScreenDocumentPreview } from "components/Document/shared/FullScreenDocumentPreview";
import {
  EpisodicEventItemFragment,
  WasteEpisodicEventNoticeKind,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatContainerDate } from "../Containers/containerDisplay";
import {
  CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION,
  DELETE_EPISODIC_EVENT_NOTICE_MUTATION,
} from "./api";
import {
  CONTACT_METHOD_LABELS,
  contactDueDate,
  episodicSubmissionRoute,
  EpisodicNotice,
  filedSubmission,
  NO_VALUE_PLACEHOLDER,
  openSubmission,
  resolveContactObligation,
  resolveFormObligation,
} from "./episodicDisplay";
import { NoticeProofDocument } from "./noticeProof";
import { RecordNoticeDialog } from "./RecordNoticeDialog";

/**
 * "View proof" for a recorded notice's attached document: fetches a fresh
 * signed inline link on click (the row only carries the document's id/title)
 * and opens it in the shared full-screen preview rather than a download.
 */
function ViewProofButton({ document }: { document: NoticeProofDocument }) {
  const alerts = useAlerts();
  const [getDocumentDownloadLink, { loading }] = useDocumentDownloadLink();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleView = async () => {
    const { data } = await getDocumentDownloadLink({
      variables: { id: document.id, inline: true },
    });
    if (data?.getDocumentDownloadLink) {
      setPreviewUrl(data.getDocumentDownloadLink);
    } else {
      alerts.error("Failed to open the proof document");
    }
  };

  return (
    <>
      <Tooltip title="View proof">
        <IconButton size="small" disabled={loading} onClick={handleView} aria-label="View proof">
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>
      <FullScreenDocumentPreview
        open={previewUrl != null}
        onClose={() => setPreviewUrl(null)}
        fileUrl={previewUrl ?? ""}
        fileExtension={document.fileExtension ?? undefined}
        title={document.title}
      />
    </>
  );
}

/**
 * Delete-with-confirm for an already-recorded notice, shared by both
 * `ContactObligationRow`'s `recorded` state and `FormObligationRow`'s
 * `filedExternally` state. Available regardless of the event's status — the
 * notice obligation stays correctable after the window ends.
 */
function useNoticeRemoval(eventId: string, kind: WasteEpisodicEventNoticeKind) {
  const alerts = useAlerts();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteNotice, { loading }] = useMutation(DELETE_EPISODIC_EVENT_NOTICE_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents"],
  });

  const handleRemove = () =>
    deleteNotice({
      variables: { eventId, kind },
      onCompleted: () => {
        alerts.success("Record removed");
        setConfirmOpen(false);
      },
      onError: () => {
        alerts.error("An error occurred while removing the record");
      },
    });

  return {
    confirmOpen,
    openConfirm: () => setConfirmOpen(true),
    closeConfirm: () => setConfirmOpen(false),
    loading,
    handleRemove,
  };
}

/**
 * "Edit record" / "Remove record" actions for a recorded notice, rendered as
 * a recorded alert's `action`. Edit reopens `RecordNoticeDialog` prefilled
 * with the notice's own date/method — saving is the same upsert, which
 * overwrites the existing notice by kind. Remove fires
 * `DELETE_EPISODIC_EVENT_NOTICE_MUTATION` behind a small confirm.
 */
function RecordedNoticeActions({
  event,
  kind,
  notice,
}: {
  event: EpisodicEventItemFragment;
  kind: WasteEpisodicEventNoticeKind;
  notice: EpisodicNotice;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const { confirmOpen, openConfirm, closeConfirm, loading, handleRemove } = useNoticeRemoval(
    event.id,
    kind
  );

  return (
    <Stack direction="row" spacing={1}>
      {notice.document && <ViewProofButton document={notice.document} />}
      <Tooltip title="Edit record">
        <IconButton size="small" onClick={() => setEditOpen(true)} aria-label="Edit record">
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Remove record">
        <IconButton size="small" onClick={openConfirm} aria-label="Remove record">
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
      <RecordNoticeDialog
        eventId={event.id}
        kind={kind}
        eventStartDate={event.startDate}
        eventType={event.type}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        initialDate={notice.noticeDate}
        initialMethod={notice.method}
        initialDocument={notice.document}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={closeConfirm}
        onConfirm={() => handleRemove()}
        loading={loading}
        header="Remove this record?"
        info="The obligation will show as unmet until you record it again."
        confirmText="Remove"
      />
    </Stack>
  );
}

/**
 * The unplanned-only 72-hour EPA-contact row: absent for planned events
 * (`resolveContactObligation` returns `null`). The card never renders a
 * judgment here — every outcome is either a plain statement of fact
 * (`recorded`, `satisfiedByForm`) or a neutral prompt (`due`/`overdue`
 * collapse to the same info alert); save-time timeliness warnings still live
 * in `RecordNoticeDialog`, and staff-facing lateness alerts are a separate,
 * later surface (ENG-16966).
 */
function ContactObligationRow({ event }: { event: EpisodicEventItemFragment }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const state = resolveContactObligation(event);

  if (!state) {
    return null;
  }

  if (state.kind === "recorded") {
    const methodLabel = state.notice.method
      ? CONTACT_METHOD_LABELS[state.notice.method]
      : NO_VALUE_PLACEHOLDER;
    return (
      <Alert
        severity="success"
        action={
          <RecordedNoticeActions
            event={event}
            kind={WasteEpisodicEventNoticeKind.EpaContact}
            notice={state.notice}
          />
        }
      >
        {`EPA contacted · ${formatContainerDate(state.notice.noticeDate)} · ${methodLabel}`}
      </Alert>
    );
  }

  if (state.kind === "satisfiedByForm") {
    return (
      <Alert severity="success">EPA contact satisfied — Form 8700-12 filed within 72 hours</Alert>
    );
  }

  return (
    <>
      <Alert
        severity="info"
        action={
          <Button size="small" onClick={() => setDialogOpen(true)}>
            Record contact
          </Button>
        }
      >
        {`Notify EPA by phone, email, or fax by ${formatContainerDate(contactDueDate(event.startDate))} — then record it here.`}
      </Alert>
      <RecordNoticeDialog
        eventId={event.id}
        kind={WasteEpisodicEventNoticeKind.EpaContact}
        eventStartDate={event.startDate}
        eventType={event.type}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}

/**
 * A completed filing: a plain statement of fact linking to the submission
 * that filed it. No timeliness judgment is rendered here — save-time
 * warnings still live in the capture dialogs/sections, and a staff-facing
 * lateness alert is a separate, later surface (ENG-16966).
 */
function FiledFormAlert({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const filed = filedSubmission(event);
  return (
    <Alert severity="success">
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
        <span>EPA notified</span>
        {filed && (
          <Button
            component={RouterLink}
            to={episodicSubmissionRoute(tenantId, filed.id, stepperEnabled)}
            target="_blank"
            rel="noopener"
            size="small"
          >
            View submission
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

/** A submission that's been created but not yet filed: links to it too. */
function SubmissionInProgressAlert({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const open = openSubmission(event);
  return (
    <Alert
      severity="info"
      action={
        open && (
          <Button
            component={RouterLink}
            to={episodicSubmissionRoute(tenantId, open.id, stepperEnabled)}
            target="_blank"
            rel="noopener"
            size="small"
          >
            View submission
          </Button>
        )
      }
    >
      Notification submission in progress
    </Alert>
  );
}

/**
 * Nothing's been filed yet: prompts a submission, with a secondary path for
 * recording a filing that already happened outside Encamp. The copy no
 * longer claims a fixed 72-hour deadline for the form itself — only the
 * EPA-contact obligation has one — but an unplanned event still gets a nudge
 * to file promptly once that contact is made.
 */
function NotFiledFormAlert({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createSubmission, { loading }] = useMutation(CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents"],
  });

  const handleCreateSubmission = async () => {
    const result = await createSubmission({ variables: { eventId: event.id } });
    const submissionId = result.data?.createEpisodicEventSubmission;
    if (submissionId) {
      // New tab so the event page (and its manual-testing context) stays put;
      // the refetch above keeps this page's row state current regardless.
      window.open(episodicSubmissionRoute(tenantId, submissionId, stepperEnabled), "_blank");
    }
  };

  return (
    <>
      <Alert
        severity="info"
        action={
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setDialogOpen(true)}>
              Record external filing
            </Button>
            <Button size="small" disabled={loading} onClick={handleCreateSubmission}>
              Create submission
            </Button>
          </Stack>
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Notification not filed — EPA Form 8700-12
        </Typography>
        <Typography variant="body2">
          Logging an episodic event doesn&apos;t file it. Create a submission to notify your
          regulator; the filing is tracked as a task.
        </Typography>
        {event.type === WasteEpisodicEventType.Unplanned && (
          <Typography variant="body2">
            There&apos;s no fixed deadline for the form — file promptly after your EPA contact.
          </Typography>
        )}
      </Alert>
      <RecordNoticeDialog
        eventId={event.id}
        kind={WasteEpisodicEventNoticeKind.FormFiledExternally}
        eventStartDate={event.startDate}
        eventType={event.type}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}

/**
 * The Form 8700-12 row, rendered for every event, dispatching on
 * `resolveFormObligation`'s priority: a completed filing wins over one still
 * in progress, then a manually recorded FORM_FILED_EXTERNALLY notice,
 * otherwise the event still needs a submission.
 */
function FormObligationRow({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const state = resolveFormObligation(event);

  if (state.kind === "filed") {
    return <FiledFormAlert event={event} tenantId={tenantId} stepperEnabled={stepperEnabled} />;
  }
  if (state.kind === "submissionInProgress") {
    return (
      <SubmissionInProgressAlert
        event={event}
        tenantId={tenantId}
        stepperEnabled={stepperEnabled}
      />
    );
  }
  if (state.kind === "filedExternally") {
    // An external filing's proof is required, but the underlying document can
    // still be deleted out from under the notice (the DB relation is
    // SetNull) — surface that as a warning with a re-attach path instead of
    // pretending the record is whole.
    const proofMissing = state.notice.document == null;
    return (
      <Alert
        severity={proofMissing ? "warning" : "success"}
        action={
          <RecordedNoticeActions
            event={event}
            kind={WasteEpisodicEventNoticeKind.FormFiledExternally}
            notice={state.notice}
          />
        }
      >
        {`Form 8700-12 filed outside Encamp · ${formatContainerDate(state.notice.noticeDate)}`}
        {proofMissing && (
          <Typography variant="body2">
            The proof document is missing — edit the record to re-attach it.
          </Typography>
        )}
      </Alert>
    );
  }
  return <NotFiledFormAlert event={event} tenantId={tenantId} stepperEnabled={stepperEnabled} />;
}

/**
 * Both of an episodic event's EPA notice obligations: the unplanned-only
 * 72-hour contact rule and the Form 8700-12 filing, each surfaced
 * independently rather than folded into one banner — an event can owe
 * either, both, or neither at any given moment.
 */
export function ObligationsSection({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">EPA notice obligations</Typography>
      <ContactObligationRow event={event} />
      <FormObligationRow event={event} tenantId={tenantId} stepperEnabled={stepperEnabled} />
    </Stack>
  );
}
